// src/runtime/shared/BrowserVaultReader.ts
// ============================================
// BrowserVaultReader - 浏览器环境下读取 Obsidian Vault 的兼容层
//
// 原理：
//   1. Vite dev server 通过自定义中间件（在 vite.config.ts 的 configureServer 中注册）
//      监听 /vault-api/* 路径，使用 Node.js fs 读取真实 Vault 文件并返回 JSON
//   2. 浏览器端通过 fetch('/vault-api/list?dir=...') 和 fetch('/vault-api/read?path=...') 调用
//   3. fetch 失败（如生产环境无中间件）时降级到 localStorage 缓存
//   4. localStorage 也没数据时返回空数组或 null（绝不抛异常）
//
// 支持的操作：
//   - listFiles(dirPath, extension?)：列出目录下的文件（可选扩展名过滤）
//   - listFilesRecursive(dirPath)：递归列出所有 .md 文件（含子目录）
//   - readFile(filePath)：读取文件内容
//   - exists(filePath)：检查文件是否存在
//   - checkAvailable()：探测中间件是否启用
//
// 缓存策略：
//   - 目录列表与文件内容分别缓存在内存中
//   - 同一会话内不重复请求同一资源
//   - clearCache() 可清空缓存强制刷新
// ============================================

/** Vault 文件元信息（与中间件返回字段对齐） */
export interface VaultFileMeta {
  /** 相对 Vault 根的路径（正斜杠分隔，如 '09_Knowledge/Cards/Agent/foo.md'） */
  path: string;
  /** 文件名（含扩展名） */
  name: string;
  /** 大小（字节） */
  size: number;
  /** 修改时间（ISO 8601） */
  mtime: string;
  /** 是否为目录 */
  isDirectory: boolean;
}

/**
 * 单例：浏览器环境下 Vault 读取器
 *
 * 使用方式：
 *   import browserVaultReader from '@/runtime/shared/BrowserVaultReader';
 *   const files = await browserVaultReader.listFiles('09_Knowledge/Cards');
 *   const content = await browserVaultReader.readFile('09_Knowledge/Cards/Agent/foo.md');
 */
export class BrowserVaultReader {
  private static instance: BrowserVaultReader | null = null;

  /** 是否为浏览器环境（有 window 和 fetch） */
  private readonly isBrowser: boolean;
  /** API 端点前缀（与 vite.config.ts 中的中间件挂载路径一致） */
  private readonly apiBase = '/vault-api';
  /** 内存缓存：目录列表（key = `${dirPath}|${extension}`） */
  private dirCache: Map<string, VaultFileMeta[]> = new Map();
  /** 内存缓存：文件内容（key = 文件相对路径） */
  private fileCache: Map<string, string> = new Map();
  /** 是否可用（中间件是否响应过；null 表示尚未探测） */
  private available: boolean | null = null;

  private constructor() {
    // 仅在浏览器环境启用：必须有 window 对象和 fetch 函数
    this.isBrowser =
      typeof window !== 'undefined' && typeof fetch === 'function';
  }

  /** 获取单例 */
  static getInstance(): BrowserVaultReader {
    if (!BrowserVaultReader.instance) {
      BrowserVaultReader.instance = new BrowserVaultReader();
    }
    return BrowserVaultReader.instance;
  }

  /** 是否为浏览器环境 */
  isBrowserEnv(): boolean {
    return this.isBrowser;
  }

  /**
   * 检查 BrowserVaultReader 是否可用
   * 通过一次 GET /vault-api/health 探测中间件是否启用
   *
   * 结果会被缓存，后续调用直接返回
   */
  async checkAvailable(): Promise<boolean> {
    if (!this.isBrowser) return false;
    if (this.available !== null) return this.available;

    try {
      const resp = await fetch(`${this.apiBase}/health`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      this.available = resp.ok;
    } catch {
      // fetch 抛异常说明中间件未注册或不可达
      this.available = false;
    }
    return this.available;
  }

  /**
   * 列出目录下的文件
   *
   * @param dirPath 相对 Vault 根的目录路径（如 '09_Knowledge/Cards'）
   * @param extension 文件扩展名过滤（默认 '.md'，传 '' 表示不过滤）
   * @returns 文件元信息数组（含子目录，便于递归遍历），失败时返回空数组
   */
  async listFiles(
    dirPath: string,
    extension: string = '.md'
  ): Promise<VaultFileMeta[]> {
    if (!this.isBrowser || !dirPath) return [];

    const cacheKey = `${dirPath}|${extension}`;
    if (this.dirCache.has(cacheKey)) {
      return this.dirCache.get(cacheKey)!;
    }

    try {
      const url =
        `${this.apiBase}/list?dir=${encodeURIComponent(dirPath)}` +
        (extension ? `&ext=${encodeURIComponent(extension)}` : '');
      const resp = await fetch(url, {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) return [];
      const json = await resp.json();
      if (!json?.success || !Array.isArray(json.files)) return [];

      const files: VaultFileMeta[] = json.files.map((f: any) => ({
        path: String(f.path || ''),
        name: String(f.name || ''),
        size: Number(f.size || 0),
        mtime: String(f.mtime || new Date().toISOString()),
        isDirectory: Boolean(f.isDirectory),
      }));

      this.dirCache.set(cacheKey, files);
      this.available = true;
      return files;
    } catch (err) {
      console.warn('[BrowserVaultReader] listFiles 失败:', dirPath, err);
      return [];
    }
  }

  /**
   * 递归列出目录下所有 .md 文件（含子目录）
   *
   * 采用 BFS 队列实现，避免深递归栈溢出。设置 5000 文件硬上限防御异常 Vault。
   *
   * @param dirPath 相对 Vault 根的目录路径
   * @returns 文件元信息数组（仅 .md 文件，不含目录）
   */
  async listFilesRecursive(dirPath: string): Promise<VaultFileMeta[]> {
    if (!this.isBrowser || !dirPath) return [];

    const result: VaultFileMeta[] = [];
    const queue: string[] = [dirPath];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);

      // 不按扩展名过滤，以便拿到子目录
      const entries = await this.listFiles(current, '');
      for (const entry of entries) {
        if (entry.isDirectory) {
          queue.push(entry.path);
        } else if (entry.name.endsWith('.md')) {
          result.push(entry);
        }
        // 防御：最多 5000 个文件
        if (result.length >= 5000) return result;
      }
    }
    return result;
  }

  /**
   * 读取文件内容
   *
   * @param filePath 相对 Vault 根的文件路径
   * @returns 文件内容字符串，失败或不存在时返回 null
   */
  async readFile(filePath: string): Promise<string | null> {
    if (!this.isBrowser || !filePath) return null;
    if (this.fileCache.has(filePath)) {
      return this.fileCache.get(filePath)!;
    }

    try {
      const resp = await fetch(
        `${this.apiBase}/read?path=${encodeURIComponent(filePath)}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!resp.ok) return null;
      const json = await resp.json();
      if (!json?.success || typeof json.content !== 'string') return null;

      this.fileCache.set(filePath, json.content);
      this.available = true;
      return json.content;
    } catch (err) {
      console.warn('[BrowserVaultReader] readFile 失败:', filePath, err);
      return null;
    }
  }

  /**
   * 写入文件到 Vault（通过 /vault-api/write 中间件）
   *
   * 用于运营层落盘、计划生成等场景。
   * 浏览器环境通过 POST 请求将内容写入 Vault 文件系统。
   *
   * @param filePath Vault 内相对路径（如 "10_Operations（运营层）/Daily/2026-07-28.md"）
   * @param content 文件内容（Markdown 字符串）
   * @returns 写入成功返回 true，失败返回 false
   */
  async writeFile(filePath: string, content: string): Promise<boolean> {
    if (!this.isBrowser || !filePath) return false;
    try {
      const resp = await fetch(
        `${this.apiBase}/write?path=${encodeURIComponent(filePath)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        }
      );
      if (!resp.ok) return false;
      const json = await resp.json();
      if (json?.success) {
        // 更新缓存
        this.fileCache.set(filePath, content);
        this.available = true;
        return true;
      }
      return false;
    } catch (err) {
      console.warn('[BrowserVaultReader] writeFile 失败:', filePath, err);
      return false;
    }
  }

  /**
   * 检查文件是否存在
   *
   * 通过读取文件来判断（中间件不提供单独的 exists 接口）
   */
  async exists(filePath: string): Promise<boolean> {
    const content = await this.readFile(filePath);
    return content !== null;
  }

  /**
   * 清空内存缓存
   *
   * 用于强制刷新数据（如 Vault 内容更新后）
   */
  clearCache(): void {
    this.dirCache.clear();
    this.fileCache.clear();
    this.available = null;
  }
}

/** 默认导出单例，便于直接 import 使用 */
export default BrowserVaultReader.getInstance();
